
import { ref, onMounted, watch, getCurrentInstance, computed, onUnmounted, defineComponent, PropType } from 'vue';
import { AIFeed } from '@/api/ai-feed';
import { AIMessage, AIMessageStatus, AIMessageType } from '@/store/modules/ai-conversation';
import { AIChatApi } from '@/api/ai-chat';
import { logForPlaySoundBtnClick, logForPlaySoundBtnShow } from '../../scripts/logger';
import { MessageAudioAutoPlayerControllerInstance, AudioStatus, AudioAutoPlayMessage } from '../../scripts/message-audio-auto-player';
import AudioPlayingIcon from '../../images/ai_audio_text_playing.png';
import AudioLoadingIcon from '../../images/ai_audio_text_loading.png';
import AudioIdelIcon from '../../images/ai_audio_text_idle.png';
import decice from '@/common/device-info';

const AudioIconMap = {
    [AudioStatus.Idle]: AudioIdelIcon,
    [AudioStatus.Loading]: AudioLoadingIcon,
    [AudioStatus.Playing]: AudioPlayingIcon,
};

const AudioPlayTextComponent = defineComponent({
    components: {},
    props: {
        msgBotId: {
            type: String,
            default: '',
        },
        messageList: {
            type: Array as PropType<AIMessage[]> | undefined,
            default: () => [],
        },
        msgTextContent: {
            type: String,
            default: '',
        },
        curBotIndex: {
            type: Number,
            default: 0,
        },
        messageIndex: {
            type: Number,
            default: 0,
        },
        messageId: {
            type: String,
            default: '',
        },
        aiFeed: {
            type: Object as PropType<AIFeed> | undefined,
            default: undefined,
        },
        botIndex: {
            type: Number,
            default: 0,
        },
    },
    setup(props) {
        const { proxy } = getCurrentInstance() || {};
        const getConversationResponseDate = ref<any>();
        const messageIndexNumber = ref<number>(0);

        const audioStatus = computed<AudioStatus>(() => {
            return MessageAudioAutoPlayerControllerInstance.status.value;
        });

        const currentPlayingMessage = computed<AudioAutoPlayMessage | null>(() => {
            return MessageAudioAutoPlayerControllerInstance.currentPlayingMessage.value;
        });

        const handleAutoPlayAudio = async () => {
            const { automaticplay } = proxy?.$store?.state?.abTestModule?.abTestParams || {};

            const messageListWithAudio: AudioAutoPlayMessage[] = (props.messageList || [])
                .slice(props.messageIndex || 0)
                .filter(item => {
                    return (
                        item?.messageStatus === AIMessageStatus.done &&
                        props?.aiFeed?.showTone &&
                        item.messageType !== AIMessageType.me &&
                        item.messageType !== AIMessageType.introducation &&
                        !!item.content
                    );
                })
                .map(item => ({
                    messageId: item?.messageId,
                    botId: item?.botId,
                    botName: props?.aiFeed?.bot_name || '',
                    messageTextContent: item?.content || '',
                    isIntro: item.messageType === AIMessageType.hi,
                }));

            const autoPlayMessageListWithAudio = automaticplay
                ? messageListWithAudio
                : ([messageListWithAudio.find(item => item.messageId === props.messageId)].filter(item => !!item) as AudioAutoPlayMessage[]);

            MessageAudioAutoPlayerControllerInstance.onAudioMessageListChange(proxy?.$isAppEnvironment ? autoPlayMessageListWithAudio : []);
            await MessageAudioAutoPlayerControllerInstance.emit({
                onPlayLoadingCallback: () => {
                    console.log('onPlayLoadingCallback');
                },
                onPLayCallback: () => {
                    console.log('onPLayCallback');
                },
                onPlayEndCallback: () => {
                    console.log('onPlayEndCallback');
                },
            });
        };

        /**
         * @zh 点击声音播放跳转至 GP
         */
        const handleGuideToGP = () => {
            const { isIOS } = decice();
            const storeLink = isIOS ? 'https://apps.apple.com/app/weeeye-novo-ia-chat/id6743759736' : 'https://fantasay.onelink.me/888I/ccwksx9g';
            window.open(storeLink, '_blank');
        };

        const handleClickAudio = (event: Event) => {
            event.stopPropagation();

            const isCurrentAudio = currentPlayingMessage.value?.messageId === props.messageId;
            if (isCurrentAudio) {
                if (audioStatus.value === AudioStatus.Playing) {
                    MessageAudioAutoPlayerControllerInstance.onPause();
                    logForPlaySoundBtnClick(props.msgBotId, 'stop', props.curBotIndex + 1, props.messageIndex + 1, props.aiFeed.conversation_id);

                    return;
                }
                if (isCurrentAudio && audioStatus.value === AudioStatus.Idle) {
                    MessageAudioAutoPlayerControllerInstance.onPlay();
                    logForPlaySoundBtnClick(props.msgBotId, 'play', props.curBotIndex + 1, props.messageIndex + 1, props.aiFeed.conversation_id);
                    return;
                }
                return;
            }

            logForPlaySoundBtnClick(props.msgBotId, 'play', props.curBotIndex + 1, props.messageIndex + 1, props.aiFeed.conversation_id);

            proxy?.$isAppEnvironment ? handleAutoPlayAudio() : handleGuideToGP();
        };

        const getAudioStatusIcon = () => {
            if (currentPlayingMessage.value?.messageId !== props.messageId) {
                return AudioIdelIcon;
            }
            return AudioIconMap[audioStatus.value];
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState !== 'visible' && MessageAudioAutoPlayerControllerInstance.status.value === AudioStatus.Playing) {
                MessageAudioAutoPlayerControllerInstance.onPause();
            }
        };

        const getConversationId = async () => {
            const data = { botId: props.aiFeed?.bot_id_str || '' };
            const res = await AIChatApi.createAIChat(data);
            getConversationResponseDate.value = res?.data;

            if (props.aiFeed.bot_id_str === getConversationResponseDate.value?.botId) {
                props.aiFeed.conversation_id = getConversationResponseDate.value?.conversationId;
                console.log('获取conversationId成功', props.aiFeed.conversation_id, props);
            }
        };

        watch(
            () => props.curBotIndex,
            async (newVal, oldVal) => {
                if (newVal === props.botIndex) {
                    await getConversationId();
                    logForPlaySoundBtnShow(props.msgBotId, props.curBotIndex + 1, props.messageIndex + 1, props.aiFeed.conversation_id);
                }
            },
            { immediate: true }
        );

        onUnmounted(() => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        });

        onMounted(() => {
            document.addEventListener('visibilitychange', handleVisibilityChange);
        });

        return {
            getAudioStatusIcon,
            handleClickAudio,
        };
    },
});

export default AudioPlayTextComponent;
